import ScoreCategoryAccordion from "./ScoreCategoryAccordion";
import * as S from "./styles";
import { TrackingEvents, ProductNames } from "@local-logic/core/tracking";
import {
  type ScoreCategories,
  ScoreCategoryTabs,
} from "@local-logic/elements-react";
import { useFlags } from "launchdarkly-react-client-sdk";
import React, { useEffect, useState } from "react";
import Attribution from "~/components/Attribution";
import { useTracking } from "~/context/trackingProvider";
import useIsMobile from "~/hooks/useIsMobile";
import useRankedCategories from "~/hooks/useRankedCategories";
import { useLocalContent } from "~/sdks/LocalContent/context";

const DesktopScores = React.forwardRef<HTMLDivElement>((_, forwardedRef) => {
  const { activeCategory, setActiveCategory } = useLocalContent();
  const flags = useFlags();
  const tracking = useTracking();
  const isMobile = useIsMobile();
  const { data } = useRankedCategories();
  const [rankedCategories, setRankedCategories] = useState(data);

  useEffect(() => {
    // The reason we're setting our own state is to trigger a re-render
    // If we're just using the "data" directly, setting the new active
    // category is completed but no re-render is done, showing the old
    // active category instead
    setRankedCategories(data);

    if (data.length > 0) {
      setActiveCategory(data[0].value);
    }
  }, [data]);

  useEffect(() => {
    // Don't track until we finished ranking the categories
    if (rankedCategories.length === 0) {
      return;
    }

    tracking.postEvent({
      eventType: TrackingEvents.LOCALCONTENT_CATEGORY_VIEWED,
      product: ProductNames.LOCAL_CONTENT,
      isMobile,
      eventAttributes: [
        {
          label: "activeCategory",
          data: activeCategory,
        },
      ],
    });
  }, [activeCategory]);

  const handleCategoryClicked = (category: ScoreCategories) => {
    setActiveCategory(category);

    tracking.postEvent({
      eventType: TrackingEvents.LOCALCONTENT_CLICKED,
      product: ProductNames.LOCAL_CONTENT,
      isMobile,
      eventAttributes: [
        {
          label: "activeCategory",
          data: category,
        },
      ],
    });
  };

  const handleAttributionClicked = () => {
    tracking.postEvent({
      eventType: TrackingEvents.LOCALCONTENT_ATTRIBUTION_CLICKED,
      product: ProductNames.LOCAL_CONTENT,
      isMobile,
    });
  };

  return (
    <ScoreCategoryTabs.Root
      options={{ currentTab: activeCategory }}
      onChange={(e) => handleCategoryClicked(e.data)}
    >
      <S.ScoresContainer
        $isScoresTabExpanded={flags.lcWellnessTab}
        ref={forwardedRef}
      >
        <S.TabsContainer>
          <ScoreCategoryTabs.Tabs category={rankedCategories} />
        </S.TabsContainer>
        <S.ScrollContainer>
          {rankedCategories.map((tabContent) => (
            <ScoreCategoryTabs.Content
              key={tabContent.value}
              options={{ value: tabContent.value }}
            >
              <ScoreCategoryAccordion
                tabContent={tabContent}
                key={tabContent.value}
              />
            </ScoreCategoryTabs.Content>
          ))}
        </S.ScrollContainer>
        <Attribution attributionClicked={handleAttributionClicked} />
      </S.ScoresContainer>
    </ScoreCategoryTabs.Root>
  );
});

DesktopScores.displayName = "DesktopScores";

export default DesktopScores;
